import { useCallback, useRef, useState, type PointerEvent } from 'react';
import { cn } from '@ridemaadi/utils';

/** Slide-to-confirm control - prevents accidental SOS sends. */
export function SlideToSendSos({
  onSend,
  disabled = false,
  loading = false,
}: {
  onSend: () => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragX, setDragX] = useState(0);
  const [sent, setSent] = useState(false);
  const dragging = useRef(false);

  const reset = () => {
    setDragX(0);
    dragging.current = false;
  };

  const complete = useCallback(() => {
    if (disabled || loading || sent) return;
    setSent(true);
    onSend();
  }, [disabled, loading, sent, onSend]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled || loading || sent) return;
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const max = rect.width - 56;
    const next = Math.max(0, Math.min(max, e.clientX - rect.left - 28));
    setDragX(next);
    if (next >= max * 0.85) complete();
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (!sent) reset();
  };

  return (
    <div
      ref={trackRef}
      className={cn(
        'relative h-14 overflow-hidden rounded-full bg-danger/15',
        disabled && 'opacity-50',
      )}
      aria-label="Slide to send SoS to riding mates"
    >
      <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-danger">
        {sent || loading ? 'Sending SOS…' : 'Slide to Send SoS to Riding mates'}
      </div>
      <div
        className={cn(
          'absolute left-1 top-1 flex h-12 w-12 items-center justify-center rounded-full bg-danger text-sm font-bold text-white shadow-md transition-transform touch-none',
          sent && 'bg-red-900',
        )}
        style={{ transform: `translateX(${dragX}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(dragX)}
      >
        SOS
      </div>
    </div>
  );
}
